import type { AuthUser } from './auth-provider.js';
import type { CrmMode } from './crm-mode-config.js';
import { CRM_PRIVILEGE_DEPTH, hasCrmPrivilege } from './crm-permissions.js';

export const CRM_MODE_OVERRIDE_KEY = 'crestCrmModeOverride';
export const CRM_MODE_CHANGED_EVENT = 'crest:crm-mode-changed';

const CRM_MODES: CrmMode[] = ['domestic', 'overseas'];

function isCrmMode(value: unknown): value is CrmMode {
  return value === 'domestic' || value === 'overseas';
}

function roleImpliesDomestic(name: string): boolean {
  return /国内|中国|domestic|china|\bcn\b/i.test(name);
}

function roleImpliesOverseas(name: string): boolean {
  return /海外|国际|overseas|international|\bintl\b|foreign/i.test(name);
}

export function getCrmModeFromRoleName(roleName: string): CrmMode | null {
  if (roleImpliesDomestic(roleName)) return 'domestic';
  if (roleImpliesOverseas(roleName)) return 'overseas';
  return null;
}

export function isSuperAdmin(user?: Partial<Pick<AuthUser, 'privileges'>> | null): boolean {
  return (
    hasCrmPrivilege(user, 'prvReadAccount', CRM_PRIVILEGE_DEPTH.Global) &&
    hasCrmPrivilege(user, 'prvReadOpportunity', CRM_PRIVILEGE_DEPTH.Global)
  );
}

export function getAvailableCrmModes(
  user?:
    | (Pick<AuthUser, 'roleNames' | 'isOverseas'> &
        Partial<
          Pick<AuthUser, 'privileges' | 'crmModeScope' | 'defaultCrmMode' | 'availableCrmModes'>
        >)
    | null,
): CrmMode[] {
  if (user?.availableCrmModes?.length) {
    return user.availableCrmModes.filter(isCrmMode);
  }
  if (user?.crmModeScope === 'both') return CRM_MODES;
  if (user?.crmModeScope === 'domestic' || user?.crmModeScope === 'overseas') {
    return [user.crmModeScope];
  }

  if (isSuperAdmin(user)) return CRM_MODES;

  const roleNames = user?.roleNames ?? [];
  const roleModes = roleNames.map(getCrmModeFromRoleName);
  const hasDomesticRole = roleModes.includes('domestic');
  const hasOverseasRole = roleModes.includes('overseas');

  if (hasDomesticRole && hasOverseasRole) return CRM_MODES;
  if (hasDomesticRole) return ['domestic'];
  if (hasOverseasRole) return ['overseas'];

  return user?.isOverseas ? ['overseas'] : ['domestic'];
}

export function getStoredCrmModeOverride(): CrmMode | null {
  try {
    const value = localStorage.getItem(CRM_MODE_OVERRIDE_KEY);
    return isCrmMode(value) ? value : null;
  } catch {
    return null;
  }
}

export function resolveUserCrmMode(
  user?:
    | (Pick<AuthUser, 'roleNames' | 'isOverseas'> &
        Partial<
          Pick<AuthUser, 'privileges' | 'crmModeScope' | 'defaultCrmMode' | 'availableCrmModes'>
        >)
    | null,
): CrmMode {
  const availableModes = getAvailableCrmModes(user);
  const storedMode = getStoredCrmModeOverride();
  if (storedMode && availableModes.includes(storedMode)) return storedMode;
  if (user?.defaultCrmMode && availableModes.includes(user.defaultCrmMode)) {
    return user.defaultCrmMode;
  }
  return availableModes[0] ?? 'domestic';
}

export function setStoredCrmModeOverride(mode: CrmMode): void {
  try {
    localStorage.setItem(CRM_MODE_OVERRIDE_KEY, mode);
    window.dispatchEvent(new CustomEvent(CRM_MODE_CHANGED_EVENT, { detail: { mode } }));
  } catch {
    // localStorage may be unavailable in private/embed contexts.
  }
}

export function clearStoredCrmModeOverride(): void {
  try {
    localStorage.removeItem(CRM_MODE_OVERRIDE_KEY);
    window.dispatchEvent(new CustomEvent(CRM_MODE_CHANGED_EVENT, { detail: { mode: null } }));
  } catch {
    // ignore
  }
}

export function normalizeStoredCrmMode(
  user?:
    | (Pick<AuthUser, 'roleNames' | 'isOverseas'> &
        Partial<
          Pick<AuthUser, 'privileges' | 'crmModeScope' | 'defaultCrmMode' | 'availableCrmModes'>
        >)
    | null,
): void {
  const storedMode = getStoredCrmModeOverride();
  if (!storedMode) return;
  if (getAvailableCrmModes(user).includes(storedMode)) return;
  clearStoredCrmModeOverride();
}
